"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFCM } from "@/hooks/use-fcm";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { hasMinEngagement, isInCooldown, markShownThisSession, recordDismiss, recordPermanentDismiss, wasShownThisSession } from "@/lib/notification-prompt-storage";
export type NotificationPromptSupport = "supported" | "granted" | "denied" | "unsupported" | "needs-install" | "unknown";
export function useNotificationPrompt() {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      preventInit: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const fcm = useFCM(t0);
  const {
    support: installSupport,
    isStandalone,
    isIOS
  } = usePwaInstall();
  const [openedByUser, setOpenedByUser] = useState(false);
  const [cooldownTick, setCooldownTick] = useState(0);
  let t1;
  bb0: {
    if (!fcm.isSupported) {
      t1 = "unsupported";
      break bb0;
    }
    if (fcm.permission === "granted") {
      t1 = "granted";
      break bb0;
    }
    if (fcm.permission === "denied") {
      t1 = "denied";
      break bb0;
    }
    if (isIOS && !isStandalone) {
      t1 = "needs-install";
      break bb0;
    }
    t1 = fcm.permission === "default" ? "supported" : "unknown";
  }
  const support = t1;
  let t2;
  if ($[1] !== fcm) {
    t2 = async () => {
      const status = await fcm.requestPermission();
      if (status === "granted") {
        await fcm.enable();
      } else {
        if (status === "denied") {
          recordPermanentDismiss();
        } else {
          recordDismiss();
        }
      }
      setCooldownTick(_temp);
      setOpenedByUser(false);
      return status;
    };
    $[1] = fcm;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const requestPermission = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const permanent = t4 === undefined ? false : t4;
      setOpenedByUser(false);
      if (permanent) {
        recordPermanentDismiss();
      } else {
        recordDismiss();
      }
      markShownThisSession();
      setCooldownTick(_temp2);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const dismiss = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setOpenedByUser(true);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const requestShow = t4;
  let t5;
  let t6;
  if ($[5] !== support) {
    t5 = () => {
      if (support === "granted") {
        setCooldownTick(_temp3);
      }
    };
    t6 = [support];
    $[5] = support;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  bb1: {
    if (openedByUser) {
      t7 = true;
      break bb1;
    }
    if (support !== "supported") {
      t7 = false;
      break bb1;
    }
    if (installSupport === "ios-safari" && !isStandalone) {
      t7 = false;
      break bb1;
    }
    if (wasShownThisSession()) {
      t7 = false;
      break bb1;
    }
    if (isInCooldown()) {
      t7 = false;
      break bb1;
    }
    if (!hasMinEngagement()) {
      t7 = false;
      break bb1;
    }
    t7 = true;
  }
  const shouldShowNudge = t7;
  let t8;
  if ($[8] !== fcm.permission || $[9] !== openedByUser || $[10] !== requestPermission || $[11] !== shouldShowNudge || $[12] !== support) {
    t8 = {
      support,
      permission: fcm.permission,
      shouldShowNudge,
      openedByUser,
      requestPermission,
      dismiss,
      requestShow
    };
    $[8] = fcm.permission;
    $[9] = openedByUser;
    $[10] = requestPermission;
    $[11] = shouldShowNudge;
    $[12] = support;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function _temp3(tick_1) {
  return tick_1 + 1;
}
function _temp2(tick_0) {
  return tick_0 + 1;
}
function _temp(tick) {
  return tick + 1;
}